import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Award, Download, Loader2,
  CheckCircle2, Clock, XCircle, AlertCircle,
} from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { attestationsService } from "services/attestations.service";
import type { Attestation, AttestationStatus } from "types/attestation.types";
import {
  Badge, EmptyState, LoadingSpinner, AnimatedList, AnimatedItem,
} from "components/dashboard";
import type { BadgeVariant } from "components/dashboard";

const C_PRIMARY = "#ff6c00";

// ─── Styled ────────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1px;
  background: #e5e7eb;
  border: 1px solid #e5e7eb;
`;
const Card = styled.div`
  background: white;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-left: 4px solid ${C_PRIMARY};
`;
const Ref = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${C_PRIMARY};
`;
const Amount = styled.p`
  font-size: 1.4rem;
  font-weight: 800;
  color: #111;
  span {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
    margin-left: 4px;
  }
`;
const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;
const MetaItem = styled.div`
  p:first-child {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #9ca3af;
    font-weight: 700;
  }
  p:last-child {
    font-size: 0.8rem;
    color: #374151;
    font-weight: 600;
  }
`;
const DlBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.55rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${C_PRIMARY};
    color: white;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  AttestationStatus,
  { label: string; variant: BadgeVariant; Icon: React.ElementType }
> = {
  active:    { label: "Active",    variant: "success", Icon: CheckCircle2 },
  pending:   { label: "En cours",  variant: "neutral", Icon: Clock        },
  revoked:   { label: "Révoquée",  variant: "error",   Icon: XCircle      },
  expired:   { label: "Expirée",   variant: "warning", Icon: AlertCircle  },
  cancelled: { label: "Annulée",   variant: "error",   Icon: XCircle      },
};

// ─── Component ─────────────────────────────────────────────────────────────────

const AttestationsPage: React.FC = () => {
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dlLoading, setDlLoading] = useState<number | null>(null);

  useEffect(() => {
    attestationsService.getMyAttestations().then((a) => {
      setAttestations(a);
      setLoading(false);
    });
  }, []);

  const handleDownload = async (id: number, studentName: string) => {
    setDlLoading(id);
    try {
      const blob = await attestationsService.downloadPDF(id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `AVI ${studentName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert("Impossible de télécharger le PDF pour l'instant.");
    } finally {
      setDlLoading(null);
    }
  };

  return (
    <DashboardLayout title="Mes attestations">
      {loading ? (
        <LoadingSpinner label="Chargement des attestations…" />
      ) : attestations.length === 0 ? (
        <EmptyState
          icon={<Award size={40} />}
          title="Aucune attestation"
          description="Vos attestations de virement apparaîtront ici une fois émises par MomsEduc."
        />
      ) : (
        <AnimatedList>
          <Grid>
            {attestations.map((att) => {
              const cfg = STATUS_CONFIG[att.status] ?? STATUS_CONFIG.pending;
              return (
                <AnimatedItem key={att.id}>
                  <Card>
                    <Ref>{att.reference}</Ref>
                    <Amount>
                      {Number(att.amount).toLocaleString("fr-FR")}
                      <span>{att.currency}</span>
                    </Amount>
                    <MetaGrid>
                      <MetaItem>
                        <p>Destination</p>
                        <p>{att.destinationCountry}</p>
                      </MetaItem>
                      <MetaItem>
                        <p>Université</p>
                        <p style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {att.university || "—"}
                        </p>
                      </MetaItem>
                      <MetaItem>
                        <p>Année académique</p>
                        <p>{att.academicYear || "—"}</p>
                      </MetaItem>
                      <MetaItem>
                        <p>Date d'émission</p>
                        <p>{new Date(att.createdAt).toLocaleDateString("fr-FR")}</p>
                      </MetaItem>
                    </MetaGrid>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Badge variant={cfg.variant}>
                        <cfg.Icon size={10} style={{ display: "inline", marginRight: 4 }} />
                        {cfg.label}
                      </Badge>
                      <DlBtn
                        onClick={() => handleDownload(att.id, att.studentName)}
                        disabled={dlLoading === att.id}
                      >
                        {dlLoading === att.id ? <Loader2 size={13} /> : <Download size={13} />}
                        PDF
                      </DlBtn>
                    </div>
                  </Card>
                </AnimatedItem>
              );
            })}
          </Grid>
        </AnimatedList>
      )}
    </DashboardLayout>
  );
};

export default AttestationsPage;
